import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import {
  caseExpensePath,
  caseTimeEntryPath,
  marketingActivityExpensePath,
  marketingActivityTimeEntryPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface ActivityEntry {
  id: string
  source_id: number
  case_id: number | null
  marketing_activity_id: number | null
  activity_type: "Time" | "Expense"
  activity_date: string
  employee_name: string | null
  case_name: string | null
  sub_type: string | null
  description: string
  notes: string | null
  added_at: string
}

interface Props {
  activities: ActivityEntry[]
  viewing_self: boolean
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

function formatShortDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function entryHref(entry: ActivityEntry): string | null {
  if (entry.marketing_activity_id) {
    return entry.activity_type === "Time"
      ? marketingActivityTimeEntryPath(
          entry.marketing_activity_id,
          entry.source_id,
        )
      : marketingActivityExpensePath(
          entry.marketing_activity_id,
          entry.source_id,
        )
  }
  if (entry.case_id) {
    return entry.activity_type === "Time"
      ? caseTimeEntryPath(entry.case_id, entry.source_id)
      : caseExpensePath(entry.case_id, entry.source_id)
  }
  return null
}

/* -- Constants -------------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Activity", href: "#" },
  { title: "Unsubmitted", href: "/employees/activity/unsubmitted" },
]

/* -- Component -------------------------------------------- */

export default function Unsubmitted({ activities, viewing_self }: Props) {
  const showByColumn = !viewing_self

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Unsubmitted Time & Expenses" />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">
          {viewing_self ? "My Unsubmitted Activity" : "Unsubmitted Activity"}
        </h1>

        {activities.length === 0 ? (
          <div className="rounded border border-green-200 bg-green-50 px-6 py-8 text-center">
            <p className="text-lg font-medium text-green-800">All caught up!</p>
            <p className="mt-1 text-sm text-green-600">
              No unsubmitted time entries or expenses.
            </p>
          </div>
        ) : (
          <div className="border-hpi-light-gray overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray text-hpi-med-gray border-b bg-gray-50 text-left text-xs font-medium tracking-wider uppercase">
                  <th className="px-4 py-2">Date</th>
                  {showByColumn && <th className="px-4 py-2">By</th>}
                  <th className="px-4 py-2">Case</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Added</th>
                </tr>
              </thead>
              <tbody className="divide-hpi-light-gray divide-y bg-white">
                {activities.map((entry) => (
                  <ActivityRow
                    key={entry.id}
                    entry={entry}
                    showByColumn={showByColumn}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

/* -- Row -------------------------------------------------- */

function ActivityRow({
  entry,
  showByColumn,
}: {
  entry: ActivityEntry
  showByColumn: boolean
}) {
  const href = entryHref(entry)
  const typeLabel = entry.sub_type
    ? `${entry.activity_type}/${entry.sub_type}`
    : entry.activity_type

  return (
    <tr className="hover:bg-hpi-pale-blue">
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{formatDate(entry.activity_date)}</CellLink>
      </td>
      {showByColumn && (
        <td className="px-4 py-2 whitespace-nowrap">
          <CellLink href={href}>{entry.employee_name ?? "-"}</CellLink>
        </td>
      )}
      <td className="px-4 py-2">
        <CellLink href={href}>{entry.case_name ?? "-"}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{typeLabel}</CellLink>
      </td>
      <td className="px-4 py-2" title={entry.notes ?? undefined}>
        <CellLink href={href}>{entry.description}</CellLink>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <CellLink href={href}>{formatShortDate(entry.added_at)}</CellLink>
      </td>
    </tr>
  )
}

/* -- Cell Link -------------------------------------------- */

function CellLink({
  href,
  children,
}: {
  href: string | null
  children: React.ReactNode
}) {
  if (!href) {
    return <span className="text-hpi-dark-gray">{children}</span>
  }
  return (
    <Link href={href} className="text-hpi-blue hover:underline" target="_blank">
      {children}
    </Link>
  )
}
